import { NgFor, NgIf } from '@angular/common';
import { Component, DestroyRef, Inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { IntegrationAdderDialogComponent } from '../integration-components/integration-adder-dialog/integration-adder-dialog.component';
import { AiConfigurationPageEventsService } from '../../../events/ai-configuration-page-events.service';
import { AiAgentResponse } from '../../../models/responses/ai-agent-response';
import { SubscriptionHelper } from '../../../classes/subscription-helper';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ConversationFlowStepResponse } from '../../../models/responses/conversation-flow-step-response';
import { ConversationStepService } from '../../../services/conversation-step.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CAL_ICON, GHL_ICON, MAKE_ICON,GOOGLE_ICON, GOOGLE_SHEET_ICON, NOTIFICATION_ICON, ZAPIER_ICON } from '../../../app.component';
import { ZapResponse } from '../../../models/responses/zap-response';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GoHighLevelIntegration } from '../../../models/responses/go-high-level-integration';
import { CalComIntegrationResponse } from '../../../models/responses/calcom-integration-response';
import { NotificationResponse } from '../../../models/responses/notification-response';
import { MakeResponse } from '../../../models/responses/make-response';
import { GoogleSheetsIntegrationResponse } from '../../../models/responses/google-sheets-integration-response';

enum IntegrationType {
  CAL = 'CAL',
  GHL = 'GHL',
  ZAPIER = 'ZAPIER',
  NOTIFICATION = 'NOTIFICATION',
  GOOGLE_SHEET = 'GOOGLE SEET'
}

interface Integration {
  id: number;
  name: string;
  IntegrationType: IntegrationType;
  addCallBack: Function;
  icon: string;
}

@Component({
  selector: 'app-action-adder-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatDivider,
    MatButtonModule,
    NgFor,
    MatIconModule,
    MatFormFieldModule,
    NgIf,
    MatTableModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './action-adder-dialog.component.html',
  styleUrl: './action-adder-dialog.component.scss'
})
export class ActionAdderDialogComponent {
  subscriptions: SubscriptionHelper;
  displayedColumns: string[] = ['Type', 'Name', "Action"];
  dataSource!: MatTableDataSource<any>;
  isLoading: boolean = false;
  allIntegrations: Integration[] = [];
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<ActionAdderDialogComponent>,
    private aiConfigurationPageEventsService: AiConfigurationPageEventsService,
    @Inject(MAT_DIALOG_DATA) public data: { aiAgent: AiAgentResponse, step: ConversationFlowStepResponse },
    private destroyRef: DestroyRef,
    private conversationStepService: ConversationStepService,
    private snackBar: MatSnackBar
  ) {
    this.subscriptions = new SubscriptionHelper(destroyRef);
    this.dataSource = new MatTableDataSource();
  }

  ngOnInit() {
    this.setAllIntegrations();
    this.dataSource.data = this.allIntegrations;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;

    this.dataSource.data = this.allIntegrations.filter(integration => integration.name.toLowerCase().includes(filterValue.toLowerCase()));
  }

  setAllIntegrations() {
    this.allIntegrations = [];
    const integrations = this.data.aiAgent.integrations;

    if (integrations.calComIntegrations && integrations.calComIntegrations.length > 0 && !this.data.step.integrations.calComIntegrations) {
      integrations.calComIntegrations.forEach(cal => {
        this.allIntegrations.push({
          id: cal.id,
          name: cal.name,
          IntegrationType: IntegrationType.CAL,
          addCallBack: () => {
            this.onAddCalIntegrationToStep(cal);
          },
          icon: CAL_ICON
        });
      });
    }

    if (integrations.goHighLevelIntegration && integrations.goHighLevelIntegration.length > 0 && !this.data.step.integrations.goHighLevelIntegration) {
      integrations.goHighLevelIntegration.forEach(ghl => {
        this.allIntegrations.push({
          id: ghl.id,
          name: ghl.name,
          IntegrationType: IntegrationType.GHL,
          addCallBack: () => {
            this.onAddGhlIntegrationToStep(ghl);
          },
          icon: GHL_ICON
        });
      });
    }

    if (integrations.notifications && integrations.notifications.length > 0 && !this.data.step.integrations.notification) {
      integrations.notifications.forEach(notification => {
        this.allIntegrations.push({
          id: notification.id,
          name: notification.target,
          IntegrationType: IntegrationType.NOTIFICATION,
          addCallBack: () => {
            this.onAddNotificationIntegrationToStep(notification);
          },
          icon: NOTIFICATION_ICON
        });
      });
    }

    if (integrations.zaps && integrations.zaps.length > 0) {
      integrations.zaps.forEach(zap => {
        this.allIntegrations.push({
          id: zap.id!,
          name: zap.name,
          IntegrationType: IntegrationType.ZAPIER,
          addCallBack: () => {
            this.onAddZapierIntegrationToStep(zap);
          },
          icon: ZAPIER_ICON
        });
      });
    }
    if (integrations.googleSheetsIntegraton) {
      const googleSheetIntegration = integrations.googleSheetsIntegraton;
      this.allIntegrations.push({
        id: googleSheetIntegration.id,
        name: 'Google Sheet integration',
        IntegrationType: IntegrationType.GOOGLE_SHEET,
        addCallBack: () => {
          this.onAddGoogleSheetIntegrationToStep(googleSheetIntegration);
        },
        icon: GOOGLE_SHEET_ICON
      })
    }

    if (integrations.makes && integrations.makes.length > 0) {
      integrations.makes.forEach(make => {
        this.allIntegrations.push({
          id: make.id!,
          name: make.name,
          IntegrationType: IntegrationType.ZAPIER,
          addCallBack: () => {
            this.onAddMakeIntegrationToStep(make);
          },
          icon: MAKE_ICON
        });
      });
    }
  }

  onAddCalIntegrationToStep(calComIntegration: CalComIntegrationResponse) {
    this.isLoading = true;
    this.conversationStepService.addCalToStep(calComIntegration.id, this.data.step.id, this.data.aiAgent.id).subscribe({
      next: (steps) => {
        this.dialogRef.close({ calComIntegration, steps });
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support');
      }
    });
  }

  onAddGhlIntegrationToStep(ghlIntegration: GoHighLevelIntegration) {
    this.isLoading = true;
    this.conversationStepService.addGHLToStep(ghlIntegration.id, this.data.step.id, this.data.aiAgent.id).subscribe({
      next: (steps) => {
        this.dialogRef.close({ ghlIntegration, steps });
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support');
      }
    });
  }

  onAddGoogleSheetIntegrationToStep(googleSheetIntegration: GoogleSheetsIntegrationResponse) {
    this.isLoading = true;
    this.conversationStepService.addGoogleSeetToStep(googleSheetIntegration.id, this.data.step.id, this.data.aiAgent.id).subscribe({
      next: (steps) => {
        this.dialogRef.close({ googleSheetIntegration, steps });
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support')
      }
    })
  }

  onAddNotificationIntegrationToStep(notificationIntegration: NotificationResponse) {
    this.isLoading = true;
    this.conversationStepService.addNotificationToStep(notificationIntegration.id, this.data.step.id, this.data.aiAgent.id).subscribe({
      next: (steps) => {
        this.dialogRef.close({ notificationIntegration, steps });
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support');
      }
    });
  }

  onAddZapierIntegrationToStep(zap: ZapResponse) {
    this.isLoading = true;
    this.conversationStepService.addZapToStep(zap.id!, this.data.step.id, this.data.aiAgent.id).subscribe({
      next: (steps) => {
        this.dialogRef.close({ zap, steps });
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support');
      }
    });
  }

  onAddMakeIntegrationToStep(make: MakeResponse) {
    this.isLoading = true;
    this.conversationStepService.addMakeToStep(make.id!, this.data.step.id,this.data.aiAgent.id).subscribe({
      next: (steps) => {
        this.dialogRef.close({make: make, steps});
      },
      error: () => {
        this.isLoading = false;
        alert('Server error contact support');
      }
    });
  }
}
